// client/src/components/BidEstimation/VendorQuotes/QuoteUploadModal.js
import React, { useState, useRef } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, LinearProgress, Box
} from '@material-ui/core'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import DescriptionIcon from '@material-ui/icons/Description'
import axios from 'axios'

export default function QuoteUploadModal ({ open, projectId, onClose, onComplete }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState(null)
  const fileInputRef = useRef()

  const handleFileSelect = (e) => {
    const selected = e.target.files[0]
    if (selected && selected.type === 'application/pdf') {
      setFile(selected)
      setError(null)
    } else {
      setError('Please select a PDF file')
    }
  }

  const handleUpload = async () => {
    if (!file) return
    setUploading(true)
    setError(null)

    try {
      const formData = new FormData()
      formData.append('quoteFile', file)
      formData.append('projectId', projectId)

      const { data } = await axios.post('/api/bid-estimation/vendor-quotes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000 // 60s — parsing can take a moment
      })

      setFile(null)
      onComplete(data)
    } catch (e) {
      const msg = e.response?.data?.error || e.message || 'Upload failed'
      setError(msg)
    }
    setUploading(false)
  }

  const handleClose = () => {
    if (uploading) return
    setFile(null)
    setError(null)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Upload Vendor Quote</DialogTitle>
      <DialogContent>
        <Box
          display='flex'
          flexDirection='column'
          alignItems='center'
          p={3}
          style={{
            border: '2px dashed #ccc',
            borderRadius: 8,
            cursor: uploading ? 'default' : 'pointer',
            backgroundColor: file ? '#f0f7ff' : '#fafafa'
          }}
          onClick={() => !uploading && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type='file'
            accept='application/pdf'
            style={{ display: 'none' }}
            onChange={handleFileSelect}
          />
          {file ? (
            <>
              <DescriptionIcon style={{ fontSize: 48, color: '#1976d2' }} />
              <Typography variant='body1' style={{ marginTop: 8 }}>{file.name}</Typography>
              <Typography variant='caption' color='textSecondary'>
                {(file.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </>
          ) : (
            <>
              <CloudUploadIcon style={{ fontSize: 48, color: '#999' }} />
              <Typography variant='body1' color='textSecondary' style={{ marginTop: 8 }}>
                Click to select a PDF quote
              </Typography>
              <Typography variant='caption' color='textSecondary'>Max 10MB</Typography>
            </>
          )}
        </Box>

        {uploading && (
          <Box mt={2}>
            <LinearProgress />
            <Typography variant='caption' color='textSecondary' align='center' display='block' mt={1}>
              Uploading and parsing...
            </Typography>
          </Box>
        )}

        {error && (
          <Typography color='error' variant='body2' style={{ marginTop: 16 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={uploading}>Cancel</Button>
        <Button
          onClick={handleUpload}
          color='primary'
          variant='contained'
          disabled={!file || uploading}
        >
          {uploading ? 'Processing...' : 'Upload & Parse'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
